import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import Dashboard from "./pages/Dashboard";
import ProjectWizard from "./pages/ProjectWizard";
import ProjectDetail from "./pages/ProjectDetail";
import FinalReportWizard from "./pages/FinalReportWizard";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import AdminPage from "./pages/AdminPage";
import SampleMemoPage from "./pages/SampleMemoPage";
import CountyLAPage from "./pages/CountyLAPage";
import CountySanDiegoPage from "./pages/CountySanDiegoPage";
import CountyBayAreaPage from "./pages/CountyBayAreaPage";
import ScrollToTop from "./components/ScrollToTop";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

// Auth Context
export const AuthContext = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.log('Not authenticated');
    } finally {
      setLoading(false);
    }
  };

  const login = (userData, token) => {
    setUser(userData);
    localStorage.setItem('token', token);
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    localStorage.removeItem('token');
  };

  return { user, setUser, loading, login, logout, checkAuth };
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const sessionId = hash.split('session_id=')[1];
      
      if (sessionId) {
        try {
          const response = await fetch(`${API}/auth/session`, {
            method: 'GET',
            headers: {
              'X-Session-ID': sessionId
            },
            credentials: 'include'
          });
          
          if (response.ok) {
            const data = await response.json();
            localStorage.setItem('token', data.session_token);
            navigate('/dashboard', { state: { user: data }, replace: true });
          } else {
            navigate('/login', { replace: true });
          }
        } catch (error) {
          console.error('Auth callback error:', error);
          navigate('/login', { replace: true });
        }
      }
    };

    processSession();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (location.state?.user) return;

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include',
          headers
        });
        
        if (!response.ok) throw new Error('Not authenticated');
        
        const userData = await response.json();
        setIsAuthenticated(true);
        setUser(userData);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    
    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return children({ user, setUser });
};

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          {({ user, setUser }) => <Dashboard user={user} setUser={setUser} />}
        </ProtectedRoute>
      } />
      <Route path="/project/new" element={
        <ProtectedRoute>
          {({ user }) => <ProjectWizard user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/project/:projectId/edit" element={
        <ProtectedRoute>
          {({ user }) => <ProjectWizard user={user} editMode={true} />}
        </ProtectedRoute>
      } />
      <Route path="/project/:projectId" element={
        <ProtectedRoute>
          {({ user }) => <ProjectDetail user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/project/:projectId/final-report" element={
        <ProtectedRoute>
          {({ user }) => <FinalReportWizard user={user} />}
        </ProtectedRoute>
      } />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/sample-memo" element={<SampleMemoPage />} />
      <Route path="/california/los-angeles-adu-permit" element={<CountyLAPage />} />
      <Route path="/california/san-diego-adu-permit" element={<CountySanDiegoPage />} />
      <Route path="/california/bay-area-adu-permit" element={<CountyBayAreaPage />} />
      <Route path="/admin" element={
        <ProtectedRoute>
          {({ user }) => <AdminPage user={user} />}
        </ProtectedRoute>
      } />
    </Routes>
  );
}

// Simplified wrapper - Google OAuth disabled until configured
function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <ScrollToTop />
        <AppRouter />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
